import type { RouterConfig } from '@nuxt/schema'
import {
	programmeRoutes,
	universityRoutes,
	scholarshipRoutes,
	type GeneratedRoute
} from '../../generated/routes'
import { RoutingDefinitions } from '../generator/types/RoutingDefinitions'
import { checkIsMobile } from '@app/composables/useMobile';
import type { RouteLocationNormalized } from 'vue-router';
import { IRouteMeta } from '@app/interfaces/dtos/IRouteMeta';

// Separate organization routes from other routes
const omniSearchRoutes: GeneratedRoute[] = []
const dynamicRoutes: GeneratedRoute[] = []
const otherRoutes: GeneratedRoute[] = []


const rootGroupedRoutes: GeneratedRoute[] = [...programmeRoutes, ...universityRoutes, ...scholarshipRoutes]

rootGroupedRoutes.forEach((route) => {
	if (route.path.includes('omni-search')) {
		omniSearchRoutes.push(route)
	}
	else if (route.name?.includes('Organisation') || route.name?.includes('City')) {
		dynamicRoutes.push(route)
	} else {
		otherRoutes.push(route)
	}
})

// Reorder: other routes first, shared dynamic routes later (lower priority)
const orderedRoutes = [...otherRoutes, ...dynamicRoutes, ...omniSearchRoutes]

type ComputedRoute = {
	name: string
	path: string
	meta: IRouteMeta
	component: () => Promise<any>
}

type NestedComputedRoute = {
	path: string
	children: ComputedRoute[]
}

const computedRoutes: ComputedRoute[] = []

type RouteQueryValue = RouteLocationNormalized['query'][string];

const normalizeValue = (value: unknown): string | string[] => {
	if (Array.isArray(value)) return value.map(String);
	if (value == null) return '';
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	return String(value);
};

const normalizeRecord = (record: Record<string, unknown>): string => {
	const entries = Object.keys(record)
		.sort()
		.map((key) => [key, normalizeValue(record[key])] as const);
	return JSON.stringify(entries);
};

const getPageQuery = (route: RouteLocationNormalized): string | undefined => {
	const pageValue = route.query.page as RouteQueryValue;

	if (Array.isArray(pageValue)) return pageValue[0] || undefined;
	if (pageValue == null) return undefined;

	return String(pageValue);
};

const isPaginationOnlyChange = (to: RouteLocationNormalized, from: RouteLocationNormalized): boolean => {
	if (getPageQuery(to) === getPageQuery(from)) return false;

	const toQuery = Object.fromEntries(
		Object.entries(to.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);
	const fromQuery = Object.fromEntries(
		Object.entries(from.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);

	const querySameWithoutPage = normalizeRecord(toQuery) === normalizeRecord(fromQuery);

	const paramsSame = normalizeRecord(to.params) ===
		normalizeRecord(from.params);

	const routeSame = to.name === from.name && to.hash === from.hash;

	return querySameWithoutPage && paramsSame && routeSame;
};

orderedRoutes.forEach((route) => {
	// Use specific component for catch-all route, otherwise use _gen.vue
	const component = route.name as RoutingDefinitions === RoutingDefinitions.CATCH_ALL
		? (): Promise<any> => import('./pages/[...catchall].vue')
		: (): Promise<any> => import('./pages/_gen.vue');

	computedRoutes.push({
		name: route.name,
		path: route.path,
		meta: route.meta,
		component,
	})
})

const nestedRouteRoots = [
	'/search',
	'/search/universities',
	'/search/scholarships',
	'/omni-search',
	'/omni-search/search/universities',
	'/omni-search/search/scholarships'
]

const toChildPath = (fullPath: string, rootPath: string): string => {
	if (fullPath === rootPath) return ''
	const rootPrefix = `${rootPath}/`
	if (!fullPath.startsWith(rootPrefix)) return fullPath
	return fullPath.slice(rootPrefix.length)
}

const nestedComputedRoutes: NestedComputedRoute[] = nestedRouteRoots
	.map((rootPath) => {
		const children = computedRoutes
			.filter(route => route.path === rootPath || route.path.startsWith(`${rootPath}/`))
			.map(route => ({
				...route,
				path: toChildPath(route.path, rootPath)
			}))

		return {
			path: rootPath,
			children
		}
	})
	.filter(route => route.children.length > 0)

const flatComputedRoutes = computedRoutes
	.filter(route => !nestedRouteRoots.some(rootPath => route.path === rootPath || route.path.startsWith(`${rootPath}/`)))

export default {
	routes: (scanned) => {
		const catchAllRoute = scanned.find(route => route.path === '/:catchall(.*)*')
		const normalizedScanned = scanned.filter(route => route.path !== '/:catchall(.*)*')

		const finalRoutes = [
			...normalizedScanned,
			...nestedComputedRoutes,
			...flatComputedRoutes,
			...(catchAllRoute ? [{ ...catchAllRoute, name: RoutingDefinitions.CATCH_ALL }] : [])
		]

		return finalRoutes
	},

	scrollBehavior: (to, from, saved) => {
		if (checkIsMobile()) return { top: 0 };
		if (saved) return saved;

		return isPaginationOnlyChange(to, from) ? { top: 0 } : false;
	}
} as RouterConfig
